import {
  getDefaultPreviewClipDurationsInFrames,
  ImageTransitionBuilder,
  ImageTransitionBuilderProps,
  ImageTransitionWrapperProps,
} from "./ImageTransitionBuilder.ts";
import { easeInOutCubic, easeInCubic } from "../../utils/easing.ts";
import { msToFrames } from "../../utils/timing.ts";
import { TransitionKind, TransitionSpeed } from "@repo/api/transition";
import { linearTiming } from "@remotion/transitions";
import { fade } from "@remotion/transitions/fade";
import { interpolate } from "remotion";

const transitionKind = TransitionKind.enum.ZOOM_IN_FADE;

export const ImageZoomInFadeTransitionBuilder: ImageTransitionBuilder<
  Record<string, unknown>
> = {
  formConfig: {
    kind: transitionKind,
    buildDefaultValue: () => ({ kind: transitionKind, speed: "NORMAL" }),
    speed: ["SLOW", "NORMAL", "FAST"],
  },

  transition: {
    getDurationInFrames: getDurationInFrames,
    buildPresentation: () => fade(),
    buildTiming: (props) =>
      linearTiming({
        durationInFrames: getDurationInFrames(props),
        easing: easeInOutCubic,
      }),
  },
  wrappers: {
    prevImageWrapper: {
      buildWrapperStyle: buildPrevImageWrapperStyle,
    },
  },

  getPreviewClipDurationsInFrames: getDefaultPreviewClipDurationsInFrames,
};

const TRANSITION_DURATIONS = {
  SLOW: 1900,
  NORMAL: 900,
  FAST: 400,
};

export function getDurationInFrames(props: ImageTransitionBuilderProps) {
  if (!props.imageTransition.speed) {
    return 0;
  }

  return msToFrames(
    TRANSITION_DURATIONS[props.imageTransition.speed],
    props.fps,
  );
}

const PREV_IMAGE_DURATIONS = {
  SLOW: 2000,
  NORMAL: 1000,
  FAST: 500,
};

function getWrapperDurationInFrames(
  speed: TransitionSpeed | undefined,
  speedMapping: Record<TransitionSpeed, number>,
  fps: number,
) {
  if (!speed) {
    return null;
  }

  return msToFrames(speedMapping[speed], fps);
}

function buildPrevImageWrapperStyle({
  fps,
  imageDurationInFrames,
  currentFrame,
  imageTransition,
}: ImageTransitionWrapperProps) {
  const transitionDurationFrames = getWrapperDurationInFrames(
    imageTransition.speed,
    PREV_IMAGE_DURATIONS,
    fps,
  );

  if (!transitionDurationFrames) {
    return null;
  }

  const endFrame = Math.max(1, imageDurationInFrames - 1);
  const startFrame = Math.max(0, endFrame - transitionDurationFrames);
  const scale = interpolate(currentFrame, [startFrame, endFrame], [1, 3.5], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInCubic,
  });

  if (currentFrame < startFrame) {
    return null;
  }

  return {
    transform: `scale(${scale})`,
  };
}
